'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ExternalLink, Package, ShoppingBag } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatArticlePrice } from '@kit/shops';
import {
  getArticlesByShopAction,
  getShopAction,
} from '@kit/shops/server/actions';
import type { Shop, ShopArticle } from '@kit/shops/types';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@kit/ui/card';
import { Skeleton } from '@kit/ui/skeleton';
import { Trans } from '@kit/ui/trans';

interface ShopTabProps {
  shopId: string;
  organizationName: string;
}

// Get localized text with fallback
function getLocalizedText(
  obj: Record<string, string> | string | null | undefined,
  locale = 'en',
): string {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  return obj[locale] || obj['en'] || Object.values(obj)[0] || '';
}

// Format price from EUR/USD price fields. Delegates to the shared
// formatArticlePrice (currency units, no /100) — see its docstring.
function formatPrice(
  priceEur: number | null,
  priceUsd: number | null,
  currency: 'EUR' | 'USD' = 'EUR',
): string {
  const price = currency === 'EUR' ? priceEur : priceUsd;
  if (price === null) return '';
  return formatArticlePrice(price, currency);
}

export function ShopTab({ shopId, organizationName }: ShopTabProps) {
  const { t } = useTranslation();
  const [shop, setShop] = useState<Shop | null>(null);
  const [articles, setArticles] = useState<ShopArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadShopData() {
      try {
        setLoading(true);
        const [shopResult, articlesResult] = await Promise.all([
          getShopAction(shopId),
          getArticlesByShopAction(shopId, { includeUnpublished: false }),
        ]);

        if (shopResult.success && shopResult.data) {
          setShop(shopResult.data);
        }

        if (articlesResult.success && articlesResult.data) {
          setArticles(articlesResult.data);
        }
      } catch (err) {
        setError('Failed to load shop data');
        console.error('Error loading shop:', err);
      } finally {
        setLoading(false);
      }
    }

    loadShopData();
  }, [shopId]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="aspect-square w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="text-muted-foreground mx-auto h-12 w-12" />
          <p className="text-muted-foreground mt-2">
            <Trans
              i18nKey="organizations:shop.unavailable"
              defaults="Shop is not available"
            />
          </p>
        </div>
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="space-y-6">
        {/* Shop Header */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              {getLocalizedText(shop.name) ||
                t('community:organization.shopName', {
                  name: organizationName,
                })}
            </CardTitle>
          </CardHeader>
          {getLocalizedText(shop.description) && (
            <CardContent>
              <p className="text-muted-foreground">
                {getLocalizedText(shop.description)}
              </p>
            </CardContent>
          )}
        </Card>

        {/* Empty State */}
        <div className="flex min-h-[200px] items-center justify-center">
          <div className="text-center">
            <Package className="text-muted-foreground mx-auto h-12 w-12" />
            <p className="text-muted-foreground mt-2">
              <Trans
                i18nKey="organizations:shop.noArticles"
                defaults="No products available yet"
              />
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Shop Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              {getLocalizedText(shop.name) ||
                t('community:organization.shopName', {
                  name: organizationName,
                })}
            </CardTitle>
            <Button variant="outline" size="sm" asChild>
              <Link href={`/shop/${shop.id}`}>
                <Trans
                  i18nKey="organizations:shop.visitShop"
                  defaults="Visit Shop"
                />
                <ExternalLink className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </CardHeader>
        {getLocalizedText(shop.description) && (
          <CardContent>
            <p className="text-muted-foreground">
              {getLocalizedText(shop.description)}
            </p>
          </CardContent>
        )}
      </Card>

      {/* Products Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5" />
            <Trans i18nKey="organizations:shop.products" defaults="Products" />
            <span className="text-muted-foreground text-sm font-normal">
              ({articles.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {articles.map((article) => {
              const articleTitle = getLocalizedText(
                article.title as unknown as Record<string, string>,
              );
              return (
                <Link
                  key={article.id}
                  href={`/shop/${shop.id}`}
                  className="group"
                >
                  <div className="space-y-2">
                    {/* Product Image */}
                    <div className="bg-muted relative aspect-square overflow-hidden rounded-lg">
                      {(article as { featured_image_id?: string | null })
                        .featured_image_id ? (
                        // eslint-disable-next-line @next/next/no-img-element -- /api/media endpoint 302s to Supabase signed / R2 media; image-rendering.md forbids next/image on media
                        <img
                          src={`/api/media/${(article as { featured_image_id?: string | null }).featured_image_id}`}
                          alt={articleTitle}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <Package className="text-muted-foreground h-12 w-12" />
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div>
                      <p className="line-clamp-2 text-sm font-medium group-hover:underline">
                        {articleTitle}
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        <Badge variant="secondary">
                          {formatPrice(article.price_eur, article.price_usd)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
